import { useMemo, useRef, useState } from 'react'
import { useSnapshot } from 'valtio'
import { options, resetOptions } from '../optionsStorage'
import { hideCurrentModal, miscUiState } from '../globalState'
import Screen from './Screen'
import { packetsReplayState } from './state/packetsReplayState'
import styles from './ReplaySettingsScreen.module.css'

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))
const toPercent = (value: number, min: number, max: number) => {
  if (max === min) return 0
  return clamp(((value - min) / (max - min)) * 100, 0, 100)
}

// ── Types ───────────────────────────────────────────────────────────────

type Quality = 'low' | 'medium' | 'high'
const QUALITY_PRESETS: Record<Quality, { rd: number, entities: boolean, smooth: boolean, starfield: boolean }> = {
  low: { rd: 3, entities: false, smooth: false, starfield: false },
  medium: { rd: 8, entities: true, smooth: true, starfield: true },
  high: { rd: 16, entities: true, smooth: true, starfield: true },
}

type ChatSize = 'off' | 'small' | 'wide'
type SubScreen = null | 'render' | 'overlay' | 'chat' | 'sound'

// The first chip used to zero the chat width ("выкл"). Now that the chat has a
// proper collapse affordance (packetsReplayState.chatCollapsed, added with
// Codex), it means "свёрнут" — fold the panel away but keep it one tab-click
// from returning, at whatever width it had.
const getChatSize = (width: number, collapsed: boolean): ChatSize =>
  collapsed ? 'off' : width >= 480 ? 'wide' : 'small'
const applyChatSize = (s: ChatSize) => {
  if (s === 'off') {
    packetsReplayState.chatCollapsed = true
    if (options.chatWidth <= 0) options.chatWidth = 320
    return
  }
  packetsReplayState.chatCollapsed = false
  options.chatWidth = s === 'small' ? 320 : 540
}

// ── Toggle-row icons (line-art, 16×16, inherit currentColor) ─────────────
const ico = (d: React.ReactNode) => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">{d}</svg>
)
const ICON = {
  smoothing: ico(<><circle cx="8" cy="8" r="3" /><path d="M8 1.5v1.5M8 13v1.5M1.5 8h1.5M13 8h1.5M3.6 3.6l1 1M11.4 11.4l1 1M12.4 3.6l-1 1M4.6 11.4l-1 1" /></>),
  entities: ico(<><circle cx="8" cy="5" r="2.3" /><path d="M3.8 13c0-2.3 1.9-3.7 4.2-3.7s4.2 1.4 4.2 3.7" /></>),
  dayNight: ico(<path d="M8 2.3a5.7 5.7 0 100 11.4A4.6 4.6 0 018 2.3z" />),
  stars: ico(<><path d="M5 2.6l.8 1.9 1.9.2-1.4 1.3.4 1.9L5 6.9 3.3 7.9l.4-1.9-1.4-1.3 1.9-.2z" /><path d="M11.5 8.5l.4 1 1 .1-.8.7.2 1-.8-.5-.8.5.2-1-.8-.7 1-.1z" /></>),
  skins: ico(<><path d="M11.2 10.5a2.4 2.4 0 00-.5-4.7A3.4 3.4 0 004.2 6 2.3 2.3 0 004.6 10.5" /><path d="M8 7.5v5M6 10.5l2 2 2-2" /></>),
  textures: ico(<><rect x="2.5" y="2.5" width="11" height="11" /><path d="M2.5 8h11M8 2.5v11" /></>),
  scoreboard: ico(<><rect x="2.5" y="2.5" width="11" height="11" /><path d="M5 6h6M5 8.3h6M5 10.6h4" /></>),
  hotbar: ico(<><rect x="2" y="6" width="3" height="4" /><rect x="6.5" y="6" width="3" height="4" /><rect x="11" y="6" width="3" height="4" /></>),
  bossbar: ico(<><rect x="2.5" y="6.3" width="11" height="3.4" /><path d="M2.5 8h6.5" strokeWidth="2.2" /></>),
  music: ico(<><path d="M6 12V4l7-1.6V10" /><circle cx="4.4" cy="12" r="1.6" /><circle cx="11.4" cy="10" r="1.6" /></>),
  // category icons (nav cards)
  catRender: ico(<><rect x="2" y="3" width="12" height="8" /><path d="M6 13.5h4M8 11v2.5" /></>),
  catOverlay: ico(<><path d="M8 2l5.6 3L8 8 2.4 5z" /><path d="M2.4 8L8 11l5.6-3" /></>),
  catChat: ico(<path d="M3 3.5h10v7H7l-3 2.5V10.5H3z" />),
  catSound: ico(<><path d="M3 6.3h2.4L8.7 3.8v8.4L5.4 9.7H3z" /><path d="M11 6a3 3 0 010 4" /></>),
}

// ── Slider field ────────────────────────────────────────────────────────

const SliderField = ({
  label, value, min, max, step = 1, onChange, valueDisplay, deadMin,
}: {
  label: string; value: number; min: number; max: number; step?: number
  onChange: (value: number) => void; valueDisplay?: string | number; deadMin?: number
}) => {
  const lo = deadMin ?? min
  const v = Math.max(lo, value)
  const fillPercent = useMemo(() => toPercent(v, min, max), [v, min, max])
  const deadPercent = deadMin ? toPercent(deadMin, min, max) : 0
  return (
    <div className={styles.sliderField}>
      <span className={styles.sliderLabel}>{label}</span>
      <div className={styles.sliderTrack}>
        <div className={styles.sliderFill} style={{ width: `${fillPercent}%` }} />
        {deadMin ? <div className={styles.sliderDead} style={{ width: `${deadPercent}%` }} /> : null}
        <div className={styles.sliderThumb} style={{ left: `${fillPercent}%` }} />
      </div>
      <span className={styles.sliderVal}>{valueDisplay ?? v}</span>
      <input
        className={styles.sliderInput}
        type="range" min={min} max={max} step={step}
        value={v}
        onChange={event => onChange(Math.max(lo, Number(event.target.value)))}
      />
    </div>
  )
}

// ── Navigation card ─────────────────────────────────────────────────────

function NavCard ({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button type="button" className={styles.navCard} onClick={onClick}>
      <span className={styles.navCardIcon}>{icon}</span>
      <span className={styles.navCardLabel}>{label}</span>
      <span className={styles.navCardArrow} aria-hidden="true" />
    </button>
  )
}

// ── FPS preset helper ──────────────────────────────────────────────────

type FpsPreset = '24' | '30' | '60' | '120' | 'auto'

// ══════════════════════════════════════════════════════════════════════════
// SUB-SCREENS
// ══════════════════════════════════════════════════════════════════════════

// ── Shared chat-style building blocks (the unified settings design language) ─

function SubHead ({ title, badge, desc, onBack, onClose }: {
  title: string; badge?: string; desc?: string; onBack?: () => void; onClose?: () => void
}) {
  const inlineClose = !!onClose && !onBack
  return (
    <>
      {onBack && (
        <div className={styles.chatTopRow}>
          <button type="button" className={styles.chatBack} onClick={onBack}>
            <span className={styles.chatChev} />{' '}
            Назад
          </button>
          {onClose && (
            <button type="button" className={styles.chatClose} onClick={onClose} aria-label="Закрыть">EXIT</button>
          )}
        </div>
      )}
      <div className={styles.chatTitleRow}>
        <span className={styles.chatDiaBig} />
        <span className={styles.chatTitle}>{title}</span>
        {badge && <span className={`${styles.chatBadge} ${inlineClose ? '' : styles.chatBadgeRight}`}>{badge}</span>}
        {inlineClose && (
          <button type="button" className={`${styles.chatClose} ${styles.chatCloseInline}`} onClick={onClose} aria-label="Закрыть">EXIT</button>
        )}
      </div>
      {desc && <div className={styles.chatHeadDesc}>{desc}</div>}
      <div className={styles.chatHr} />
    </>
  )
}

function ChatSection ({ label, desc, children }: { label?: string; desc?: string; children: React.ReactNode }) {
  return (
    <>
      <div className={styles.chatSec}>
        {label && (
          <div className={styles.chatSecHead}>
            <span className={styles.secDotSm} />
            <span className={styles.chatSecLbl}>{label}</span>
          </div>
        )}
        {desc && <div className={styles.chatSecDesc}>{desc}</div>}
        {children}
      </div>
      <div className={styles.chatHr} />
    </>
  )
}

type ChatSegOption<T> = { val: T; label: string; icon?: React.ReactNode; tone?: string }
function ChatSeg<T extends string> ({ options: opts, value, onChange, compact = false }: {
  options: Array<ChatSegOption<T>>; value: T; onChange: (v: T) => void; compact?: boolean
}) {
  return (
    <div className={`${styles.chatSeg} ${compact ? styles.chatSegCompact : ''}`}>
      {opts.map(o => {
        const on = value === o.val
        // per-option tone (3-state colours, like the fps widget) when selected.
        // All chips share ONE width — the old per-option weight made the rows
        // asymmetric, which read as broken rather than as accent.
        const style: React.CSSProperties = {}
        if (on && o.tone) {
          style.color = o.tone
          style.borderColor = `${o.tone}8c`
          // tone-tinted plate (keeps the relief instead of a flat color slab)
          style.background = `linear-gradient(180deg, ${o.tone}2e, ${o.tone}14 60%, rgba(18, 26, 41, 0.6))`
          style.boxShadow = `inset 0 1px ${o.tone}2e, inset 0 -1px rgba(2, 8, 15, 0.4), 0 1px 3px rgba(0, 0, 0, 0.26), 0 0 8px ${o.tone}24`
        }
        return (
          <button
            key={o.val}
            type="button"
            className={`${styles.chatChip} ${on ? styles.chatChipOn : ''}`}
            style={style}
            onClick={() => onChange(o.val)}
          >
            {o.icon}
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

const QUALITY_DESC: Record<Quality, string> = {
  low: 'Пониженное качество — отключены сущности и сглаживание ради производительности',
  medium: 'Сбалансированный профиль — оптимально для большинства реплеев',
  high: 'Максимальное качество — все эффекты включены, выше нагрузка',
}

// ── Stepped ruler-slider (e.g. fps): clickable values + draggable track ──
type StepStop<T> = { val: T; label: string; color: string }
function StepSlider<T extends string> ({ stops, value, onChange }: {
  stops: Array<StepStop<T>>; value: T; onChange: (v: T) => void
}) {
  const trackRef = useRef<HTMLDivElement>(null)
  const n = stops.length
  const idx = Math.max(0, stops.findIndex(s => s.val === value))
  const pos = (i: number) => (n > 1 ? (i / (n - 1)) * 100 : 0)
  const pickAt = (clientX: number) => {
    const el = trackRef.current
    if (!el || n < 2) return
    const r = el.getBoundingClientRect()
    const p = Math.max(0, Math.min(1, (clientX - r.left) / r.width))
    onChange(stops[Math.round(p * (n - 1))].val)
  }
  const onDown = (e: React.PointerEvent) => {
    pickAt(e.clientX)
    const move = (ev: PointerEvent) => pickAt(ev.clientX)
    const up = () => {
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', up)
    }
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', up)
  }
  return (
    <div className={styles.stepSlider} onPointerDown={onDown}>
      <div className={styles.stepLabels}>
        {stops.map((s, i) => (
          <button
            key={s.val}
            type="button"
            className={`${styles.stepLabel} ${i === idx ? styles.stepLabelOn : ''}`}
            style={{ left: `${pos(i)}%`, color: i === idx ? s.color : undefined }}
            onClick={e => { e.stopPropagation(); onChange(s.val) }}
          >{s.label}</button>
        ))}
      </div>
      <div ref={trackRef} className={styles.stepTrack}>
        {/* minor ruler ticks between the labelled stops */}
        {stops.slice(0, -1).map((s, i) => (
          <span key={`m${s.val}`} className={styles.stepMinor} style={{ left: `${pos(i) + 100 / (n - 1) / 2}%` }} />
        ))}
        {/* dim the part of the coloured ruler past the selected value */}
        <div className={styles.stepDim} style={{ left: `${pos(idx)}%` }} />
        {/* major colour-coded ticks */}
        {stops.map((s, i) => (
          <span
            key={s.val}
            className={`${styles.stepTick} ${i === idx ? styles.stepTickOn : ''}`}
            style={{ left: `${pos(i)}%`, color: s.color, background: s.color }}
          />
        ))}
        <span className={styles.stepThumb} style={{ left: `${pos(idx)}%`, background: stops[idx].color }} />
      </div>
    </div>
  )
}

const FPS_STOPS: Array<StepStop<FpsPreset>> = [
  { val: '24', label: '24', color: '#e05555' },
  { val: '30', label: '30', color: '#e0904b' },
  { val: '60', label: '60', color: '#d8c24b' },
  { val: '120', label: '120', color: '#8fd96b' },
  { val: 'auto', label: 'Авто', color: '#6fb6e0' },
]

function RenderSubScreen ({ onBack, onClose }: { onBack: () => void; onClose: () => void }) {
  const snap = useSnapshot(options)
  const { replayMinecraftVersion } = useSnapshot(packetsReplayState)
  const { singleplayer, gameLoaded } = useSnapshot(miscUiState)

  const isSingleplayer = singleplayer || !gameLoaded
  const renderDistanceValue = isSingleplayer ? snap.renderDistance : snap.multiplayerRenderDistance
  const renderDistanceMax = isSingleplayer ? 16 : 12

  const frameLimitNum = typeof snap.frameLimit === 'number' ? snap.frameLimit : 0
  const fpsPresetNums: Record<string, number> = { '24': 24, '30': 30, '60': 60, '120': 120 }
  const currentFps: FpsPreset = frameLimitNum ? (
    Object.entries(fpsPresetNums).find(([, n]) => n === frameLimitNum)?.[0] as FpsPreset ?? 'auto'
  ) : 'auto'

  const setFps = (v: FpsPreset) => {
    options.frameLimit = v === 'auto' ? false : Number(v)
  }

  return (
    <>
      <SubHead title="Рендер" badge={`MC ${replayMinecraftVersion ?? '?'}`} desc="Качество картинки, производительность и эффекты мира" onBack={onBack} onClose={onClose} />

      <ChatSection label="Кадров в секунду">
        <StepSlider<FpsPreset> stops={FPS_STOPS} value={currentFps} onChange={setFps} />
        <SliderField
          label="Дальность"
          value={renderDistanceValue}
          min={2} max={renderDistanceMax} step={2}
          onChange={v => {
            if (isSingleplayer) options.renderDistance = v
            else options.multiplayerRenderDistance = v
          }}
        />
      </ChatSection>

      <ChatSection label="Производительность">
        <ChatToggleRow
          icon={ICON.smoothing}
          title="Сглаживание"
          desc="Смягчает перепады освещения на блоках"
          value={snap.smoothLighting}
          onChange={v => { options.smoothLighting = v }}
        />
        <ChatToggleRow
          icon={ICON.entities}
          title="Сущности"
          desc="Отображать мобов, предметы и игроков"
          value={snap.renderEntities}
          onChange={v => { options.renderEntities = v }}
        />
      </ChatSection>

      <ChatSection label="Мир">
        <ChatToggleRow
          icon={ICON.dayNight}
          title="Цикл дня и ночи"
          desc="Динамическое освещение от положения солнца"
          value={snap.dayCycleAndLighting}
          onChange={v => { options.dayCycleAndLighting = v }}
        />
        <ChatToggleRow
          icon={ICON.stars}
          title="Звёздное небо"
          desc="Фон с звёздами в ночное время"
          value={snap.starfieldRendering}
          onChange={v => { options.starfieldRendering = v }}
        />
      </ChatSection>

      <ChatSection label="Дополнительно">
        <ChatToggleRow
          icon={ICON.skins}
          title="Загружать скины"
          desc="Загружать скины игроков из сети"
          value={snap.loadPlayerSkins}
          onChange={v => { options.loadPlayerSkins = v }}
        />
        <ChatToggleRow
          icon={ICON.textures}
          title="Упрощённые текстуры"
          desc="Отключить текстуры блок-сущностей (сундуки, таблички)"
          value={snap.disableBlockEntityTextures}
          onChange={v => { options.disableBlockEntityTextures = v }}
        />
      </ChatSection>
    </>
  )
}

function OverlaySubScreen ({ onBack, onClose }: { onBack: () => void; onClose: () => void }) {
  const snap = useSnapshot(options)
  const disabled = snap.disabledUiParts

  const toggleUiPart = (name: string) => {
    const next = disabled.includes(name)
      ? disabled.filter(x => x !== name)
      : [...disabled, name]
    options.disabledUiParts = next
  }

  return (
    <>
      <SubHead title="Оверлей" desc="Элементы игрового интерфейса поверх мира" onBack={onBack} onClose={onClose} />

      <ChatSection label="Интерфейс">
        <SliderField
          label="Масштаб"
          value={snap.guiScale}
          min={1} max={4} step={1}
          deadMin={2}
          onChange={v => { options.guiScale = v }}
        />
        <SliderField
          label="Прозрачность"
          value={snap.chatOpacity}
          min={0} max={100}
          onChange={v => { options.chatOpacity = v }}
        />
      </ChatSection>

      <ChatSection label="Игровой HUD">
        <ChatToggleRow
          icon={ICON.scoreboard}
          title="Счёт"
          desc="Таблица счёта в правой части экрана"
          value={!disabled.includes('scoreboard')}
          onChange={() => toggleUiPart('scoreboard')}
        />
        <ChatToggleRow
          icon={ICON.hotbar}
          title="Хотбар"
          desc="Панель быстрого доступа с предметами"
          value={!disabled.includes('hotbar')}
          onChange={() => toggleUiPart('hotbar')}
        />
        <ChatToggleRow
          icon={ICON.bossbar}
          title="Боссбар"
          desc="Полоса здоровья босса"
          value={!disabled.includes('bossbars')}
          onChange={() => toggleUiPart('bossbars')}
        />
      </ChatSection>
    </>
  )
}

function ChatSubScreen ({ onBack, onClose }: { onBack: () => void; onClose: () => void }) {
  const snap = useSnapshot(options)
  const replaySnap = useSnapshot(packetsReplayState)

  const chatSize = getChatSize(snap.chatWidth, replaySnap.chatCollapsed)

  return (
    <>
      <SubHead title="Чат" badge="Реплей" desc="Окно сообщений и что в нём показывать" onBack={onBack} onClose={onClose} />

      <ChatSection label="Размер чата">
        <ChatSeg<ChatSize>
          options={[
            { val: 'off', label: 'Свёрнут', tone: '#6fb6e0', icon: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M10 4l-4 4 4 4M14 4l-4 4 4 4" /></svg> },
            { val: 'small', label: 'Малый', tone: '#e0a050', icon: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M4 6h6M4 9h4" /></svg> },
            { val: 'wide', label: 'Широкий', tone: '#8fd96b', icon: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M3 5h10M3 8h10M3 11h7" /></svg> },
          ]}
          value={chatSize}
          onChange={applyChatSize}
        />
      </ChatSection>

      {/* ── Показывать в чате ── */}
      <div className={styles.chatSec}>
        <div className={styles.chatSecHead}>
          <span className={styles.secDotSm} />
          <span className={styles.chatSecLbl}>Показывать в чате</span>
        </div>

        <ChatToggleRow
          icon={<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M3 3.5h10v7H7l-3 2.5V10.5H3z"/></svg>}
          title="Сообщения персонажа"
          desc="Все реплики и диалоги от лица игрока"
          value={snap.displayLoadingMessages}
          onChange={v => { options.displayLoadingMessages = v }}
          descGreen
        />
        <ChatToggleRow
          icon={<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M5.5 4.5L2.5 8l3 3.5M10.5 4.5l3 3.5-3 3.5"/></svg>}
          title="Команды от персонажа"
          value={true}
          onChange={() => {}}
        />
        <ChatToggleRow
          icon={<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M2.5 4h8v5H6L3.5 11V9H2.5z"/><path d="M7 5.5h6.5v4.5H12v1.5L10 10"/></svg>}
          title="Сообщения других игроков"
          value={true}
          onChange={() => {}}
        />
        <ChatToggleRow
          icon={<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3"><rect x="2.5" y="3.5" width="11" height="7"/><path d="M6 13h4M8 10.5V13"/></svg>}
          title="Системные сообщения"
          value={true}
          onChange={() => {}}
        />
      </div>

      <div className={styles.chatHr} />

      {/* ── Настройки чата ── */}
      <div className={styles.chatSec}>
        <div className={styles.chatSecHead}>
          <span className={styles.secDotSm} />
          <span className={styles.chatSecLbl}>Настройки чата</span>
        </div>
        <ChatToggleRow
          icon={<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M8 3v6M5.5 6.5L8 9l2.5-2.5M3.5 12h9"/></svg>}
          title="Скрывать, если чат пустой"
          desc="Окно чата исчезает, если в записи нет сообщений"
          value={snap.chatHideWhenEmpty}
          onChange={v => { options.chatHideWhenEmpty = v }}
          descGreen
        />
      </div>

      <div className={styles.chatHr} />

      {/* ── footer ── */}
      <div className={styles.chatFoot}>
        <span className={styles.chatFootL}>
          <span className={styles.footDiaSm} />{' '}
          6 параметров
        </span>
        <span className={styles.chatFootR}>Replay Engine</span>
      </div>
    </>
  )
}

// ── Chat toggle row with icon ──────────────────────────────────────────

function ChatToggleRow ({ icon, title, desc, value, onChange, descGreen }: {
  icon?: React.ReactNode; title: string; desc?: string
  value: boolean; onChange: (v: boolean) => void; descGreen?: boolean
}) {
  return (
    <div className={`${styles.optRow} ${value ? styles.optRowOn : ''}`}>
      <span className={styles.optIco}>{icon ?? <span className={styles.optIcoDia} />}</span>
      <span className={styles.optTxt}>
        <span className={styles.optT}>{title}</span>
        {desc && <span className={`${styles.optD} ${descGreen ? styles.optDGreen : ''}`}>{desc}</span>}
      </span>
      <div
        className={`${styles.togBig} ${value ? styles.togBigOn : ''}`}
        onClick={() => onChange(!value)}
        role="switch"
        aria-checked={value}
        tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(!value) } }}
      >
        <span className={styles.togBigKnob} />
      </div>
    </div>
  )
}

function SoundSubScreen ({ onBack, onClose }: { onBack: () => void; onClose: () => void }) {
  const snap = useSnapshot(options)

  return (
    <>
      <SubHead title="Звук" desc="Громкость эффектов и фоновой музыки" onBack={onBack} onClose={onClose} />

      <ChatSection label="Громкость">
        <SliderField
          label="Общая"
          value={snap.volume}
          min={0} max={100}
          valueDisplay={`${snap.volume}%`}
          onChange={v => { options.volume = v }}
        />
      </ChatSection>

      <ChatSection label="Музыка">
        <ChatToggleRow
          icon={ICON.music}
          title="Музыка"
          desc="Фоновая музыка в игре"
          value={snap.enableMusic}
          onChange={v => { options.enableMusic = v }}
        />
        <SliderField
          label="Громкость"
          value={snap.musicVolume}
          min={0} max={100}
          valueDisplay={`${snap.musicVolume}%`}
          onChange={v => { options.musicVolume = v }}
        />
      </ChatSection>
    </>
  )
}

// ══════════════════════════════════════════════════════════════════════════
// MAIN SCREEN
// ══════════════════════════════════════════════════════════════════════════

export default function ReplaySettingsScreen ({ backButtonAction }: { backButtonAction?: () => void }) {
  const [subScreen, setSubScreen] = useState<SubScreen>(null)
  const replaySnap = useSnapshot(packetsReplayState)
  const { replayMinecraftVersion } = replaySnap
  const snap = useSnapshot(options)
  const { singleplayer, gameLoaded } = useSnapshot(miscUiState)
  const closeSettings = backButtonAction ?? (() => hideCurrentModal())

  const isSingleplayer = singleplayer || !gameLoaded
  const renderDistanceValue = isSingleplayer ? snap.renderDistance : snap.multiplayerRenderDistance
  const renderDistanceMin = 2
  const renderDistanceMax = isSingleplayer ? 16 : 12

  // Compute active quality
  const activeQuality: Quality = snap.renderEntities
    ? ((snap.smoothLighting && renderDistanceValue >= (isSingleplayer ? 14 : 10)) ? 'high' : 'medium')
    : 'low'

  const applyQuality = (q: Quality) => {
    const preset = QUALITY_PRESETS[q]
    const rd = q === 'high' ? renderDistanceMax : Math.min(preset.rd, renderDistanceMax)
    if (isSingleplayer) options.renderDistance = rd
    else options.multiplayerRenderDistance = rd
    options.renderEntities = preset.entities
    options.smoothLighting = preset.smooth
    options.starfieldRendering = preset.starfield
  }

  const chatSize = getChatSize(snap.chatWidth, replaySnap.chatCollapsed)

  const handleReset = () => {
    if (confirm('Сбросить все настройки?')) resetOptions()
  }

  const mainOptionCount = 4
  const allOptionCount = Object.keys(snap).length

  const hubBody = (
    <>
      <SubHead title="Настройки" badge="Реплей" onClose={closeSettings} />

      <ChatSection label="Качество рендера" desc={QUALITY_DESC[activeQuality]}>
        <ChatSeg<Quality>
          options={[
            { val: 'low', label: 'Лёгкий', tone: '#e05555' },
            { val: 'medium', label: 'Баланс', tone: '#E8B84B' },
            { val: 'high', label: 'Максимум', tone: '#8fd96b' },
          ]}
          value={activeQuality}
          onChange={applyQuality}
          compact
        />
        <SliderField
          label="Обзор"
          value={snap.fov}
          min={30} max={110}
          onChange={v => { options.fov = v }}
        />
        <SliderField
          label="Дальность"
          value={renderDistanceValue}
          min={renderDistanceMin} max={renderDistanceMax} step={2}
          onChange={v => {
            if (isSingleplayer) options.renderDistance = v
            else options.multiplayerRenderDistance = v
          }}
        />
      </ChatSection>

      <ChatSection label="Чат" desc="Размер и видимость окна сообщений в реплее">
        <ChatSeg<ChatSize>
          options={[
            { val: 'off', label: 'Свёрнут', tone: '#6fb6e0' },
            { val: 'small', label: 'Малый', tone: '#e0a050' },
            { val: 'wide', label: 'Широкий', tone: '#8fd96b' },
          ]}
          value={chatSize}
          onChange={applyChatSize}
          compact
        />
      </ChatSection>

      <ChatSection label="Разделы" desc="Тонкая настройка по категориям">
        <div className={styles.navGrid}>
          <NavCard icon={ICON.catRender} label="Рендер" onClick={() => setSubScreen('render')} />
          <NavCard icon={ICON.catOverlay} label="Оверлеи" onClick={() => setSubScreen('overlay')} />
          <NavCard icon={ICON.catChat} label="Чат" onClick={() => setSubScreen('chat')} />
          <NavCard icon={ICON.catSound} label="Звук" onClick={() => setSubScreen('sound')} />
        </div>
      </ChatSection>

      <ChatSection>
        <div className={`${styles.field} ${styles.fieldStatic}`}>
          <span className={styles.fieldLabel}>Версия</span>
          <span className={styles.fieldValue}>
            {replayMinecraftVersion ? `Minecraft ${replayMinecraftVersion}` : 'Minecraft ?'}
          </span>
        </div>
        <button
          type="button"
          className={styles.emergencyBtn}
          onClick={handleReset}
        >
          <span className={styles.emergencyBtnIcon}>⚡</span>
          <span className={styles.emergencyBtnText}>Сбросить настройки</span>
        </button>
      </ChatSection>

      <div className={styles.chatFoot}>
        <span className={styles.chatFootL}>
          <span className={styles.footDiaSm} aria-hidden="true" />
          {mainOptionCount} разделов
        </span>
        <span className={styles.chatFootR}>
          ◆ {allOptionCount} опций · Replay Engine
        </span>
      </div>
    </>
  )

  const subBody = subScreen === null ? null
    : subScreen === 'render' ? <RenderSubScreen onBack={() => setSubScreen(null)} onClose={closeSettings} />
      : subScreen === 'overlay' ? <OverlaySubScreen onBack={() => setSubScreen(null)} onClose={closeSettings} />
        : subScreen === 'chat' ? <ChatSubScreen onBack={() => setSubScreen(null)} onClose={closeSettings} />
          : <SoundSubScreen onBack={() => setSubScreen(null)} onClose={closeSettings} />

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 520
  const mobileContentStyle = isMobile ? {
    width: '94vw',
    maxWidth: '94vw',
    marginTop: 0,
    alignSelf: 'flex-start' as const,
    minHeight: 0,
    // The replay bottom bar collapses while settings are mounted; do not reserve
    // its full height here or the settings panel loses usable vertical room.
    height: 'calc(100dvh - 24px - env(safe-area-inset-bottom, 0px))',
    overflowY: 'auto' as const,
    paddingBottom: 'calc(30px + env(safe-area-inset-bottom, 0px))',
    boxSizing: 'border-box' as const,
  } : { width: 'min(94vw, calc(472px * var(--ui-scale, 1) * 0.72))' }

  return (
    <Screen title="" className={styles.screen} contentStyle={mobileContentStyle}>
      <div className={styles.dismiss} onClick={closeSettings} aria-hidden="true" />
      <div className={styles.stage}>
        {/* the open window (hub, or a sub-screen) stays in normal flow → centred
            by its own height. When a sub-screen is open the hub becomes a dimmed
            shadow shifted to the LEFT behind it. */}
        {subScreen !== null && (
          <div className={`${styles.panel} ${styles.panelBack}`} aria-hidden="true">
            <div className={styles.panelSurface}>{hubBody}</div>
            <div className={styles.backDim} aria-hidden="true" />
          </div>
        )}
        <div className={`${styles.panel} ${subScreen === null ? '' : styles.panelFront}`}>
          <div className={styles.panelSurface}>{subScreen === null ? hubBody : subBody}</div>
        </div>
      </div>
    </Screen>
  )
}
